import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { 
  Trash2, AlertTriangle, Database, RefreshCw, CheckCircle2, 
  History, Undo2, Upload, Edit, Settings, User, ChevronLeft, ChevronRight,
  FileText, Package, Users, Loader2, DatabaseZap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping for action types
const actionIcons = {
  upload_sales: Upload,
  upload_products: Package,
  upload_warehouse_stock: Database,
  upload_combos: Package,
  upload_sku_mappings: FileText,
  update_inventory: Edit,
  update_parents: Edit,
  edit_product: Edit,
  manage_suppliers: Settings,
  manage_settings: Settings,
  undo_action: Undo2,
  login: User,
  default: History
};

// Color mapping for action types
const actionColors = {
  upload_sales: 'bg-green-100 text-green-700 border-green-200',
  upload_products: 'bg-blue-100 text-blue-700 border-blue-200',
  upload_warehouse_stock: 'bg-purple-100 text-purple-700 border-purple-200',
  upload_combos: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  upload_sku_mappings: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  update_inventory: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  update_parents: 'bg-orange-100 text-orange-700 border-orange-200',
  edit_product: 'bg-gray-100 text-gray-700 border-gray-200',
  undo_action: 'bg-red-100 text-red-700 border-red-200',
  default: 'bg-gray-100 text-gray-700 border-gray-200'
};

export default function DataManagementPage() {
  const [dataSummary, setDataSummary] = useState(null);
  const [activityLog, setActivityLog] = useState({ activities: [], total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, platform: null, platformName: '' });
  const [clearAllDialog, setClearAllDialog] = useState(false);
  const [undoDialog, setUndoDialog] = useState({ open: false, activity: null });
  const [indexStatus, setIndexStatus] = useState({ checking: false, creating: false, hasIndex: null });
  const [indexDiagnostic, setIndexDiagnostic] = useState({ open: false, loading: false, data: null });
  const [deleting, setDeleting] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [activityPage, setActivityPage] = useState(1);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    fetchDataSummary();
    fetchActivityLog(1);
    checkIndexStatus();
    fetchSyncStatus();
  }, []);

  const fetchSyncStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/sync-status`);
      setSyncStatus(res.data);
      return res.data;
    } catch (error) {
      setSyncStatus(null);
      return null;
    }
  };

  const startSync = async () => {
    setSyncConfirmOpen(false);
    setSyncing(true);
    try {
      await axios.post(`${API}/admin/sync-from-source`);
      toast({ title: 'Sync started', description: 'Copying live data into preview. This can take a minute or two...' });
      const poll = setInterval(async () => {
        const s = await fetchSyncStatus();
        if (s && s.state && !s.state.running) {
          clearInterval(poll);
          setSyncing(false);
          if (s.state.error) {
            toast({ title: 'Sync failed', description: s.state.error, variant: 'destructive' });
          } else {
            toast({ title: 'Sync complete', description: 'Preview now mirrors the live app data.' });
            fetchDataSummary();
            fetchActivityLog(1);
          }
        }
      }, 2000);
    } catch (error) {
      setSyncing(false);
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to start sync', variant: 'destructive' });
    }
  };

  const fetchDataSummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/sales/data-summary`);
      setDataSummary(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load data summary',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLog = async (page = 1) => {
    setActivityLoading(true);
    try {
      const response = await axios.get(`${API}/activity?page=${page}&page_size=20`);
      setActivityLog(response.data);
      setActivityPage(page);
    } catch (error) {
      console.error('Failed to load activity log:', error);
    } finally {
      setActivityLoading(false);
    }
  };

  const handleClearPlatform = async () => {
    if (!deleteDialog.platform) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/sales/clear-platform/${deleteDialog.platform}`);
      toast({
        title: 'Success',
        description: response.data.message,
      });
      fetchDataSummary();
      fetchActivityLog(1);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to clear data',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
      setDeleteDialog({ open: false, platform: null, platformName: '' });
    }
  };

  const handleClearAll = async () => {
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/sales/clear-all`);
      toast({
        title: 'Success',
        description: response.data.message,
      });
      fetchDataSummary();
      fetchActivityLog(1);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to clear data',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
      setClearAllDialog(false);
    }
  };

  const handleUndo = async () => {
    if (!undoDialog.activity?.batch_id) return;
    
    setUndoing(true);
    try {
      const response = await axios.delete(`${API}/activity/undo/${undoDialog.activity.batch_id}`);
      toast({
        title: 'Success',
        description: response.data.message,
      });
      fetchDataSummary();
      fetchActivityLog(activityPage);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to undo action',
        variant: 'destructive',
      });
    } finally {
      setUndoing(false);
      setUndoDialog({ open: false, activity: null });
    }
  };

  const checkIndexStatus = async () => {
    setIndexStatus(prev => ({ ...prev, checking: true }));
    try {
      const response = await axios.get(`${API}/sales/check-index`);
      setIndexStatus({ checking: false, creating: false, hasIndex: response.data.has_unique_index });
    } catch (error) {
      console.error('Failed to check index status:', error);
      setIndexStatus(prev => ({ ...prev, checking: false }));
    }
  };

  const createIndex = async () => {
    setIndexStatus(prev => ({ ...prev, creating: true }));
    try {
      const response = await axios.post(`${API}/sales/create-index`);
      if (response.data.success) {
        toast({
          title: 'Success',
          description: response.data.message,
        });
        setIndexStatus({ checking: false, creating: false, hasIndex: true });
      } else {
        toast({
          title: 'Cannot Create Index',
          description: response.data.message,
          variant: 'destructive',
        });
        setIndexStatus(prev => ({ ...prev, creating: false }));
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create index',
        variant: 'destructive',
      });
      setIndexStatus(prev => ({ ...prev, creating: false }));
    }
  };

  const fixIndex = async () => {
    setIndexStatus(prev => ({ ...prev, creating: true }));
    try {
      const response = await axios.post(`${API}/sales/fix-index`);
      if (response.data.success) {
        toast({
          title: 'Index Fixed!',
          description: response.data.message,
        });
        setIndexStatus({ checking: false, creating: false, hasIndex: true });
        // Refresh diagnostic data
        fetchIndexDiagnostic();
      } else {
        toast({
          title: 'Cannot Fix Index',
          description: response.data.message,
          variant: 'destructive',
        });
        setIndexStatus(prev => ({ ...prev, creating: false }));
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to fix index',
        variant: 'destructive',
      });
      setIndexStatus(prev => ({ ...prev, creating: false }));
    }
  };

  const fetchIndexDiagnostic = async () => {
    setIndexDiagnostic({ open: true, loading: true, data: null });
    try {
      const response = await axios.get(`${API}/sales/diagnose-index`);
      setIndexDiagnostic({ open: true, loading: false, data: response.data });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to fetch diagnostic',
        variant: 'destructive',
      });
      setIndexDiagnostic({ open: false, loading: false, data: null });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    // Ensure the date string is parsed as UTC if no timezone specified
    let date;
    if (dateStr.endsWith('Z') || dateStr.includes('+') || dateStr.includes('-', 10)) {
      date = new Date(dateStr);
    } else {
      // If no timezone info, treat as UTC
      date = new Date(dateStr + 'Z');
    }
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata'
    });
  };

  const getActionIcon = (actionType) => {
    const Icon = actionIcons[actionType] || actionIcons.default;
    return <Icon className="w-4 h-4" />;
  };

  const getActionColor = (actionType) => {
    return actionColors[actionType] || actionColors.default;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <p className="text-muted-foreground">Loading data summary...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight font-heading" data-testid="page-title">
              Data Management
            </h1>
            <p className="text-muted-foreground mt-2">
              View activity history, manage data, and undo specific actions.
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => { fetchDataSummary(); fetchActivityLog(1); }} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => setClearAllDialog(true)}
              data-testid="clear-all-btn"
              disabled={!dataSummary?.total_records}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear All Data
            </Button>
          </div>
        </div>

        {/* Sync from Live App (admin-only, preview environment only) */}
        {user?.role === 'admin' && syncStatus?.enabled && (
          <div className="border border-blue-200 bg-blue-50/50 rounded-sm p-4" data-testid="sync-from-live-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <DatabaseZap className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-bold tracking-tight font-heading">Sync Data from Live App</h3>
                  <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                    Replace this preview environment's data with a fresh snapshot from{' '}
                    <span className="font-mono text-foreground">{syncStatus.source_url}</span>. Your live app is only
                    read — production is never modified. Login accounts are preserved.
                  </p>
                  {(syncing || syncStatus.state?.running) && (
                    <p className="text-sm text-blue-700 mt-2 flex items-center gap-2" data-testid="sync-progress">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Syncing… {syncStatus.state?.progress?.slice(-1)[0] || 'starting'}
                    </p>
                  )}
                  {!syncing && !syncStatus.state?.running && syncStatus.state?.error && (
                    <p className="text-sm text-red-600 mt-2" data-testid="sync-error">Last sync error: {syncStatus.state.error}</p>
                  )}
                  {!syncing && !syncStatus.state?.running && !syncStatus.state?.error && syncStatus.state?.finished_at && (
                    <p className="text-sm text-green-700 mt-2 flex items-center gap-2" data-testid="sync-done">
                      <CheckCircle2 className="w-4 h-4" />
                      Last sync completed — {Object.keys(syncStatus.state?.summary || {}).length} collections updated.
                    </p>
                  )}
                </div>
              </div>
              <Button
                onClick={() => setSyncConfirmOpen(true)}
                disabled={syncing || syncStatus.state?.running}
                data-testid="sync-from-live-btn"
              >
                {(syncing || syncStatus.state?.running)
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <DatabaseZap className="w-4 h-4 mr-2" />}
                Sync Now
              </Button>
            </div>
            {Object.keys(syncStatus.state?.summary || {}).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {Object.entries(syncStatus.state.summary).map(([name, v]) => (
                  <Badge key={name} variant="outline" className="font-mono text-xs">{name}: {v.local}</Badge>
                ))}
              </div>
            )}
          </div>
        )}

        <Tabs defaultValue="activity" className="w-full">
          <TabsList>
            <TabsTrigger value="activity" className="flex items-center gap-2">
              <History className="w-4 h-4" />
              Activity History
            </TabsTrigger>
            <TabsTrigger value="data" className="flex items-center gap-2">
              <Database className="w-4 h-4" />
              Sales Data
            </TabsTrigger>
          </TabsList>

          {/* Activity History Tab */}
          <TabsContent value="activity" className="mt-4">
            <div className="border border-border bg-card rounded-sm">
              <div className="p-4 border-b border-border">
                <h2 className="text-lg font-bold tracking-tight font-heading flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Activity Log
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  View all actions taken in the app. Actions with the Undo button can be reversed.
                </p>
              </div>

              {activityLoading ? (
                <div className="p-8 text-center text-muted-foreground">Loading activity log...</div>
              ) : activityLog.activities.length > 0 ? (
                <>
                  <div className="divide-y divide-border">
                    {activityLog.activities.map((activity) => (
                      <div key={activity.id} className="p-4 hover:bg-muted/30 flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`p-2 rounded-sm border ${getActionColor(activity.action_type)}`}>
                            {getActionIcon(activity.action_type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{activity.action_description}</p>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                {activity.action_type.replace(/_/g, ' ')}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {activity.affected_records} records
                              </span>
                              <span className="text-xs text-muted-foreground">•</span>
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {activity.user_email}
                              </span>
                            </div>
                            {activity.metadata && (
                              <div className="mt-1 text-xs text-muted-foreground">
                                {activity.metadata.platform && <span>Platform: {activity.metadata.platform}</span>}
                                {activity.metadata.filename && <span className="ml-2">File: {activity.metadata.filename}</span>}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDate(activity.timestamp)}
                          </span>
                          {activity.can_undo && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setUndoDialog({ open: true, activity })}
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                              data-testid={`undo-${activity.batch_id}`}
                            >
                              <Undo2 className="w-3 h-3 mr-1" />
                              Undo
                            </Button>
                          )}
                          {activity.undone_at && (
                            <Badge variant="secondary" className="text-xs">
                              Undone
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {activityLog.total_pages > 1 && (
                    <div className="p-4 border-t border-border flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        Page {activityLog.page} of {activityLog.total_pages} ({activityLog.total} total)
                      </span>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fetchActivityLog(activityPage - 1)}
                          disabled={activityPage <= 1}
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fetchActivityLog(activityPage + 1)}
                          disabled={activityPage >= activityLog.total_pages}
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="p-8 text-center">
                  <History className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No activity recorded yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Actions like uploads and edits will appear here
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Sales Data Tab */}
          <TabsContent value="data" className="mt-4 space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border border-border bg-card rounded-sm p-4">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Total Platforms</p>
                <p className="text-2xl font-bold font-mono">{dataSummary?.total_platforms || 0}</p>
              </div>
              <div className="border border-border bg-card rounded-sm p-4">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Total Records</p>
                <p className="text-2xl font-bold font-mono">{dataSummary?.total_records?.toLocaleString() || 0}</p>
              </div>
              <div className="border border-border bg-card rounded-sm p-4">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Upload History</p>
                <p className="text-2xl font-bold font-mono">{dataSummary?.upload_history?.length || 0} uploads</p>
              </div>
            </div>

            {/* Duplicate Prevention Status */}
            <div className="border border-border bg-card rounded-sm p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-warning" />
                    Duplicate Prevention Status
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {indexStatus.checking ? (
                      'Checking...'
                    ) : indexStatus.hasIndex === true ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Active - Duplicate uploads will be automatically skipped
                      </span>
                    ) : indexStatus.hasIndex === false ? (
                      <span className="text-orange-600 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Inactive - Duplicate records can be inserted!
                      </span>
                    ) : (
                      'Checking status...'
                    )}
                  </p>
                </div>
                <div className="flex gap-2">
                  {indexStatus.hasIndex === false && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={createIndex}
                      disabled={indexStatus.creating}
                      data-testid="create-index-btn"
                    >
                      {indexStatus.creating ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        'Enable Duplicate Prevention'
                      )}
                    </Button>
                  )}
                  {indexStatus.hasIndex === true && (
                    <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                      Protected
                    </Badge>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchIndexDiagnostic}
                    data-testid="diagnose-index-btn"
                  >
                    View Index Details
                  </Button>
                </div>
              </div>
            </div>

            {/* Platform Data Table */}
            <div className="border border-border bg-card rounded-sm">
              <div className="p-4 border-b border-border">
                <h2 className="text-lg font-bold tracking-tight font-heading flex items-center gap-2">
                  <Database className="w-4 h-4" />
                  Sales Data by Platform
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Click "Clear" to remove all data for a platform before re-uploading correct data
                </p>
              </div>
              
              {dataSummary?.platforms?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-muted-foreground">Platform</th>
                        <th className="text-right p-3 text-xs uppercase tracking-widest text-muted-foreground">Records</th>
                        <th className="text-right p-3 text-xs uppercase tracking-widest text-muted-foreground">Sales Units</th>
                        <th className="text-right p-3 text-xs uppercase tracking-widest text-muted-foreground">Return Units</th>
                        <th className="text-right p-3 text-xs uppercase tracking-widest text-muted-foreground">Net Units</th>
                        <th className="text-center p-3 text-xs uppercase tracking-widest text-muted-foreground">Date Range</th>
                        <th className="text-center p-3 text-xs uppercase tracking-widest text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dataSummary.platforms.map((platform) => (
                        <tr key={platform.platform_id} className="border-b border-border hover:bg-muted/30">
                          <td className="p-3">
                            <span className="font-medium">{platform.platform_name}</span>
                            <br />
                            <span className="text-xs text-muted-foreground font-mono">{platform.platform_id}</span>
                          </td>
                          <td className="p-3 text-right font-mono">{platform.total_records.toLocaleString()}</td>
                          <td className="p-3 text-right font-mono text-success">{platform.sales_units.toLocaleString()}</td>
                          <td className="p-3 text-right font-mono text-orange-500">{platform.return_units.toLocaleString()}</td>
                          <td className="p-3 text-right font-mono font-bold">{platform.net_units.toLocaleString()}</td>
                          <td className="p-3 text-center text-sm text-muted-foreground">
                            {platform.date_range_start} to {platform.date_range_end}
                          </td>
                          <td className="p-3 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => setDeleteDialog({
                                open: true,
                                platform: platform.platform_id,
                                platformName: platform.platform_name
                              })}
                              data-testid={`clear-${platform.platform_id}`}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Clear
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center">
                  <CheckCircle2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No sales data uploaded yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Go to Upload Sales to add data
                  </p>
                </div>
              )}
            </div>

            {/* Upload History */}
            {dataSummary?.upload_history?.length > 0 && (
              <div className="border border-border bg-card rounded-sm">
                <div className="p-4 border-b border-border">
                  <h2 className="text-lg font-bold tracking-tight font-heading">Upload History</h2>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-card">
                      <tr className="border-b border-border bg-muted/50">
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-muted-foreground">Filename</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-muted-foreground">Platform</th>
                        <th className="text-right p-3 text-xs uppercase tracking-widest text-muted-foreground">Records</th>
                        <th className="text-center p-3 text-xs uppercase tracking-widest text-muted-foreground">Data Range</th>
                        <th className="text-center p-3 text-xs uppercase tracking-widest text-muted-foreground">Uploaded</th>
                        <th className="text-center p-3 text-xs uppercase tracking-widest text-muted-foreground">By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dataSummary.upload_history.map((upload, idx) => (
                        <tr key={idx} className="border-b border-border hover:bg-muted/30">
                          <td className="p-3 text-sm font-mono max-w-xs truncate" title={upload.filename}>
                            {upload.filename}
                          </td>
                          <td className="p-3 text-sm">{upload.platform}</td>
                          <td className="p-3 text-right font-mono">{upload.records_count}</td>
                          <td className="p-3 text-center text-sm text-muted-foreground">
                            {upload.date_range_start?.substring(0, 10) || 'N/A'} to {upload.date_range_end?.substring(0, 10) || 'N/A'}
                          </td>
                          <td className="p-3 text-center text-sm text-muted-foreground">
                            {formatDate(upload.uploaded_at)}
                          </td>
                          <td className="p-3 text-center text-sm text-muted-foreground">
                            {upload.uploaded_by || 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Clear Platform Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !deleting && setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Clear {deleteDialog.platformName} Data?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete ALL sales and returns data for <strong>{deleteDialog.platformName}</strong>.
              <br /><br />
              You can then re-upload the correct data without duplicates.
              <br /><br />
              <span className="text-destructive font-medium">This action cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearPlatform}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Clearing...' : 'Yes, Clear Data'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear All Dialog */}
      <AlertDialog open={clearAllDialog} onOpenChange={(open) => !deleting && setClearAllDialog(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Clear ALL Sales Data?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete ALL sales and returns data from ALL platforms.
              <br /><br />
              <span className="text-destructive font-medium">
                This is a destructive action and cannot be undone. All {dataSummary?.total_records?.toLocaleString()} records will be deleted.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearAll}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Clearing...' : 'Yes, Delete Everything'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sync from Live Confirmation Dialog */}
      <AlertDialog open={syncConfirmOpen} onOpenChange={setSyncConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace preview data with live data?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the current preview data and loads a fresh snapshot from{' '}
              <span className="font-mono">{syncStatus?.source_url}</span>. Your live/production app will NOT be
              changed (read-only). Login accounts are preserved. This can take a minute or two.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sync-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={startSync} data-testid="sync-confirm-btn">
              Yes, replace preview data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Undo Action Dialog */}
      <AlertDialog open={undoDialog.open} onOpenChange={(open) => !undoing && setUndoDialog({ ...undoDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Undo2 className="w-5 h-5 text-orange-500" />
              Undo This Action?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <strong>Action:</strong> {undoDialog.activity?.action_description}
              <br /><br />
              <strong>Records affected:</strong> {undoDialog.activity?.affected_records}
              <br />
              <strong>Performed by:</strong> {undoDialog.activity?.user_email}
              <br />
              <strong>Date:</strong> {formatDate(undoDialog.activity?.timestamp)}
              <br /><br />
              <span className="text-orange-600 font-medium">
                This will remove/revert all data from this action.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={undoing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUndo}
              disabled={undoing}
              className="bg-orange-500 text-white hover:bg-orange-600"
            >
              {undoing ? 'Undoing...' : 'Yes, Undo Action'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Index Diagnostic Dialog */}
      <Dialog open={indexDiagnostic.open} onOpenChange={(open) => setIndexDiagnostic(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Index Diagnostic Information</DialogTitle>
            <DialogDescription>
              Technical details about the duplicate prevention index configuration
            </DialogDescription>
          </DialogHeader>
          
          {indexDiagnostic.loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <span className="ml-3">Loading diagnostic data...</span>
            </div>
          ) : indexDiagnostic.data ? (
            <div className="space-y-4 text-sm">
              <div className="bg-muted p-3 rounded-sm">
                <p className="font-medium">Total Records: <span className="font-mono">{indexDiagnostic.data.total_records?.toLocaleString()}</span></p>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Index Configuration:</h4>
                <pre className="bg-muted p-3 rounded-sm overflow-x-auto text-xs">
                  {JSON.stringify(indexDiagnostic.data.indexes, null, 2)}
                </pre>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Sample Records (field values):</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border">
                    <thead className="bg-muted">
                      <tr>
                        <th className="p-2 text-left border">Platform</th>
                        <th className="p-2 text-left border">SKU</th>
                        <th className="p-2 text-left border">Order ID</th>
                        <th className="p-2 text-left border">Sale Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {indexDiagnostic.data.sample_records?.map((rec, idx) => (
                        <tr key={idx}>
                          <td className="p-2 border font-mono">{rec.platform || 'null'}</td>
                          <td className="p-2 border font-mono">{rec.sku || 'null'}</td>
                          <td className="p-2 border font-mono">{rec.order_id || 'null'}</td>
                          <td className="p-2 border font-mono">{rec.sale_date || 'null'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Null/Empty Field Counts:</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {indexDiagnostic.data.null_field_counts && Object.entries(indexDiagnostic.data.null_field_counts).map(([key, value]) => (
                    <div key={key} className="flex justify-between bg-muted p-2 rounded">
                      <span>{key}:</span>
                      <span className={`font-mono ${value > 0 ? 'text-orange-600 font-bold' : ''}`}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 p-3 rounded-sm">
                <p className="text-blue-700 text-xs">
                  <strong>Correct Index Fields:</strong> sku + platform + date + quantity
                </p>
                <p className="text-blue-600 text-xs mt-1">
                  If your index uses different fields (like order_id or sale_date), click "Fix Index" below.
                </p>
              </div>
              
              <div className="flex justify-end pt-2">
                <Button
                  variant="default"
                  onClick={() => {
                    setIndexDiagnostic(prev => ({ ...prev, open: false }));
                    fixIndex();
                  }}
                  disabled={indexStatus.creating}
                  data-testid="fix-index-btn"
                >
                  {indexStatus.creating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Fixing Index...
                    </>
                  ) : (
                    'Fix Index (Drop & Recreate)'
                  )}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
